import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from '@/components/charts/recharts';
import { ArrowRight, AlertCircle, Wallet, CalendarClock, TrendingDown, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FREQUENCIES = [
  { label: 'Weekly', value: 'weekly', periodsPerYear: 52 },
  { label: 'Monthly', value: 'monthly', periodsPerYear: 12 },
  { label: 'Quarterly', value: 'quarterly', periodsPerYear: 4 },
  { label: 'Yearly', value: 'yearly', periodsPerYear: 1 },
];

export default function SWPCalculator() {
  const [initialInvestment, setInitialInvestment] = useState(5000000);
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState(60000);
  const [expectedReturn, setExpectedReturn] = useState(10);
  const [durationYears, setDurationYears] = useState(15);
  const [frequency, setFrequency] = useState('monthly');
  const [annualIncrease, setAnnualIncrease] = useState(6);

  const [initialInvestmentInput, setInitialInvestmentInput] = useState('50,00,000');
  const [withdrawalInput, setWithdrawalInput] = useState('60,000');
  const [returnInput, setReturnInput] = useState('10');
  const [durationInput, setDurationInput] = useState('15');
  const [annualIncreaseInput, setAnnualIncreaseInput] = useState('6');

  const [errors, setErrors] = useState({});
  const [tableVisibleRows, setTableVisibleRows] = useState(15);

  const formatIndianNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '';
    const numStr = Math.round(num).toString();
    let lastThree = numStr.substring(numStr.length - 3);
    let otherNumbers = numStr.substring(0, numStr.length - 3);
    if (otherNumbers !== '') lastThree = ',' + lastThree;
    return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
  };

  const parseInteger = (str) => {
    if (!str) return 0;
    return parseInt(str.replace(/,/g, ''), 10) || 0;
  };

  const formatCurrency = (value) => {
    const safeValue = Math.max(0, Math.round(value || 0));
    if (safeValue >= 10000000) return `₹${(safeValue / 10000000).toFixed(2)} Cr`;
    if (safeValue >= 100000) return `₹${(safeValue / 100000).toFixed(2)} L`;
    return `₹${formatIndianNumber(safeValue)}`;
  };

  const selectedFrequency = useMemo(
    () => FREQUENCIES.find((f) => f.value === frequency) || FREQUENCIES[1],
    [frequency]
  );

  const withdrawalPerPeriod = useMemo(() => {
    const periods = selectedFrequency.periodsPerYear;
    if (periods === 12) return monthlyWithdrawal;
    if (periods === 52) return (monthlyWithdrawal * 12) / 52;
    if (periods === 4) return monthlyWithdrawal * 3;
    return monthlyWithdrawal * 12;
  }, [monthlyWithdrawal, selectedFrequency.periodsPerYear]);

  const simulate = useCallback(
    (startingWithdrawalPerPeriod) => {
      const periodsPerYear = selectedFrequency.periodsPerYear;
      const totalPeriods = durationYears * periodsPerYear;
      const periodRate = Math.pow(1 + expectedReturn / 100, 1 / periodsPerYear) - 1;

      let corpus = initialInvestment;
      let totalWithdrawn = 0;
      let withdrawalThisPeriod = startingWithdrawalPerPeriod;
      const yearlyData = [];
      let runOutPeriodIndex = null;

      for (let periodIndex = 1; periodIndex <= totalPeriods; periodIndex++) {
        corpus = corpus * (1 + periodRate);

        const withdrawAmount = Math.min(corpus, withdrawalThisPeriod);
        corpus = corpus - withdrawAmount;
        totalWithdrawn += withdrawAmount;

        if (runOutPeriodIndex === null && corpus <= 0.000001) {
          runOutPeriodIndex = periodIndex;
        }

        const isYearEnd = periodIndex % periodsPerYear === 0;
        if (isYearEnd) {
          const year = periodIndex / periodsPerYear;
          yearlyData.push({
            year: `Year ${year}`,
            yearNum: year,
            corpus: Math.max(0, Math.round(corpus)),
            totalWithdrawn: Math.max(0, Math.round(totalWithdrawn)),
          });

          withdrawalThisPeriod = withdrawalThisPeriod * (1 + annualIncrease / 100);
        }

        if (corpus <= 0.000001) {
          corpus = 0;
        }
      }

      return {
        corpusLeft: Math.max(0, Math.round(corpus)),
        totalWithdrawn: Math.max(0, Math.round(totalWithdrawn)),
        yearlyData,
        runOutPeriodIndex,
        periodsPerYear,
      };
    },
    [annualIncrease, durationYears, expectedReturn, initialInvestment, selectedFrequency.periodsPerYear]
  );

  const results = useMemo(() => simulate(withdrawalPerPeriod), [simulate, withdrawalPerPeriod]);
  const visibleYearRows = useMemo(
    () => results.yearlyData.slice(0, Math.min(tableVisibleRows, results.yearlyData.length)),
    [results.yearlyData, tableVisibleRows]
  );

  const safeWithdrawalPerPeriod = useMemo(() => {
    const targetPeriods = durationYears * selectedFrequency.periodsPerYear;
    if (initialInvestment <= 0 || targetPeriods <= 0) return 0;

    const isFeasible = (candidate) => {
      const sim = simulate(candidate);
      return sim.runOutPeriodIndex === null;
    };

    let low = 0;
    let high = Math.max(withdrawalPerPeriod, initialInvestment);

    // Ensure high is infeasible (or we hit a cap)
    for (let i = 0; i < 40 && isFeasible(high); i++) {
      high = high * 1.5;
      if (high > initialInvestment * 10) break;
    }

    for (let i = 0; i < 50; i++) {
      const mid = (low + high) / 2;
      if (isFeasible(mid)) low = mid;
      else high = mid;
    }

    return low;
  }, [durationYears, initialInvestment, selectedFrequency.periodsPerYear, simulate, withdrawalPerPeriod]);

  const safeMonthlyWithdrawal = useMemo(() => {
    const periods = selectedFrequency.periodsPerYear;
    if (periods === 12) return safeWithdrawalPerPeriod;
    if (periods === 52) return (safeWithdrawalPerPeriod * 52) / 12;
    if (periods === 4) return safeWithdrawalPerPeriod / 3;
    return safeWithdrawalPerPeriod / 12;
  }, [safeWithdrawalPerPeriod, selectedFrequency.periodsPerYear]);

  const runOutText = useMemo(() => {
    if (results.runOutPeriodIndex === null) return `Does not run out within ${durationYears} years`;
    const periods = results.periodsPerYear;
    const years = Math.floor(results.runOutPeriodIndex / periods);
    const remainder = results.runOutPeriodIndex % periods;
    const extraPeriods = remainder === 0 ? 0 : remainder;

    if (extraPeriods === 0) return `After ${years} years`;
    if (periods === 12) return `After ${years} years ${extraPeriods} months`;
    if (periods === 52) return `After ${years} years ${extraPeriods} weeks`;
    if (periods === 4) return `After ${years} years ${extraPeriods} quarters`;
    return `After ${years} years`;
  }, [results.runOutPeriodIndex, results.periodsPerYear, durationYears]);

  const insights = useMemo(() => {
    const messages = [];

    if (results.runOutPeriodIndex === null) {
      messages.push(`Your corpus may last ${durationYears}+ years at current withdrawal rate.`);
    } else {
      const approxYears = results.runOutPeriodIndex / results.periodsPerYear;
      const rounded = approxYears >= 1 ? approxYears.toFixed(1) : approxYears.toFixed(2);
      messages.push(`At the current withdrawal rate, your corpus may run out in ~${rounded} years.`);
    }

    if (annualIncrease > 0) {
      messages.push('Increasing withdrawals by inflation may reduce longevity.');
    }

    if (monthlyWithdrawal > safeMonthlyWithdrawal * 1.05) {
      messages.push('Consider lower withdrawal for longer sustainability.');
    }

    return messages.slice(0, 3);
  }, [annualIncrease, durationYears, monthlyWithdrawal, results.periodsPerYear, results.runOutPeriodIndex, safeMonthlyWithdrawal]);

  const handleAmountChange = (setter, inputSetter, key, min, max) => (e) => {
    const raw = e.target.value.replace(/,/g, '');
    inputSetter(e.target.value);
    const num = parseInt(raw, 10);
    if (!isNaN(num) && num >= min && num <= max) {
      setter(num);
      setErrors((prev) => ({ ...prev, [key]: '' }));
    } else if (raw === '') {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    } else {
      setErrors((prev) => ({ ...prev, [key]: `Enter a value between ₹${formatIndianNumber(min)} and ₹${formatIndianNumber(max)}` }));
    }
  };

  const handleAmountBlur = (setter, inputValue, inputSetter, key, min, max, fallback) => () => {
    const num = parseInteger(inputValue);
    const clamped = Math.max(min, Math.min(max, num || fallback));
    setter(clamped);
    inputSetter(formatIndianNumber(clamped));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const handlePercentChange = (setter, inputSetter, key, min, max) => (e) => {
    inputSetter(e.target.value);
    const num = parseFloat(e.target.value);
    if (!isNaN(num) && num >= min && num <= max) {
      setter(num);
      setErrors((prev) => ({ ...prev, [key]: '' }));
    } else if (e.target.value === '') {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    } else {
      setErrors((prev) => ({ ...prev, [key]: `Value must be between ${min}% and ${max}%` }));
    }
  };

  const handlePercentBlur = (setter, inputValue, inputSetter, key, min, max, fallback) => () => {
    const num = parseFloat(inputValue);
    const clamped = Math.max(min, Math.min(max, isNaN(num) ? fallback : num));
    setter(clamped);
    inputSetter(clamped.toString());
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-4">
            SWP Calculator
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            SWP Calculator – Plan Monthly Income from Mutual Funds
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="glass-effect rounded-2xl p-8">
            <h2 className="font-heading text-2xl font-bold text-text-primary mb-6">Withdrawal Details</h2>

            <div className="space-y-6">
              {/* Initial Investment */}
              <div>
                <label className="block text-text-secondary text-sm mb-2">Initial Investment Amount (₹)</label>
                <input
                  type="text"
                  value={initialInvestmentInput}
                  onChange={handleAmountChange(setInitialInvestment, setInitialInvestmentInput, 'initialInvestment', 10000, 100000000)}
                  onBlur={handleAmountBlur(setInitialInvestment, initialInvestmentInput, setInitialInvestmentInput, 'initialInvestment', 10000, 100000000, 5000000)}
                  onFocus={() => setInitialInvestmentInput(initialInvestment.toString())}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors.initialInvestment ? 'border-red-500' : 'border-white/10'}`}
                  data-testid="swp-initial-investment"
                />
                {errors.initialInvestment && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {errors.initialInvestment}
                  </p>
                )}
                <input
                  type="range"
                  min="10000"
                  max="100000000"
                  step="10000"
                  value={initialInvestment}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setInitialInvestment(value);
                    setInitialInvestmentInput(formatIndianNumber(value));
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              {/* Monthly Withdrawal */}
              <div>
                <label className="block text-text-secondary text-sm mb-2">Monthly Withdrawal Amount (₹)</label>
                <input
                  type="text"
                  value={withdrawalInput}
                  onChange={handleAmountChange(setMonthlyWithdrawal, setWithdrawalInput, 'withdrawal', 1, 10000000)}
                  onBlur={handleAmountBlur(setMonthlyWithdrawal, withdrawalInput, setWithdrawalInput, 'withdrawal', 1, 10000000, 60000)}
                  onFocus={() => setWithdrawalInput(monthlyWithdrawal.toString())}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors.withdrawal ? 'border-red-500' : 'border-white/10'}`}
                  data-testid="swp-monthly-withdrawal"
                />
                {errors.withdrawal && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {errors.withdrawal}
                  </p>
                )}
                <input
                  type="range"
                  min="1"
                  max="1000000"
                  step="1000"
                  value={monthlyWithdrawal}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setMonthlyWithdrawal(value);
                    setWithdrawalInput(formatIndianNumber(value));
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              {/* Expected Return */}
              <div>
                <label className="block text-text-secondary text-sm mb-2">Expected Annual Return (%) <span className="text-text-muted">Range: 2% to 12%</span></label>
                <input
                  type="text"
                  value={returnInput}
                  onChange={handlePercentChange(setExpectedReturn, setReturnInput, 'return', 2, 12)}
                  onBlur={handlePercentBlur(setExpectedReturn, returnInput, setReturnInput, 'return', 2, 12, 10)}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors.return ? 'border-red-500' : 'border-white/10'}`}
                  data-testid="swp-expected-return"
                />
                {errors.return && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {errors.return}
                  </p>
                )}
                <input
                  type="range"
                  min="2"
                  max="12"
                  step="0.1"
                  value={expectedReturn}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    setExpectedReturn(value);
                    setReturnInput(value.toString());
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              {/* Duration */}
              <div>
                <label className="block text-text-secondary text-sm mb-2">Withdrawal Duration (years) <span className="text-text-muted">Range: 1 to 75</span></label>
                <input
                  type="text"
                  value={durationInput}
                  onChange={(e) => {
                    setDurationInput(e.target.value);
                    const num = parseInt(e.target.value, 10);
                    if (!isNaN(num) && num >= 1 && num <= 75) {
                      setDurationYears(num);
                      setErrors((prev) => ({ ...prev, duration: '' }));
                    } else if (e.target.value === '') {
                      setErrors((prev) => ({ ...prev, duration: '' }));
                    } else {
                      setErrors((prev) => ({ ...prev, duration: 'Duration must be between 1 and 75 years' }));
                    }
                  }}
                  onBlur={() => {
                    const num = parseInt(durationInput, 10);
                    const clamped = Math.max(1, Math.min(75, isNaN(num) ? 15 : num));
                    setDurationYears(clamped);
                    setDurationInput(clamped.toString());
                    setErrors((prev) => ({ ...prev, duration: '' }));
                  }}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors.duration ? 'border-red-500' : 'border-white/10'}`}
                  data-testid="swp-duration"
                />
                {errors.duration && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {errors.duration}
                  </p>
                )}
                <input
                  type="range"
                  min="1"
                  max="75"
                  step="1"
                  value={durationYears}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setDurationYears(value);
                    setDurationInput(value.toString());
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-text-secondary text-sm mb-2">Withdrawal Frequency</label>
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="swp-frequency"
                >
                  {FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value} className="bg-[#1a1f2e]">
                      {f.label}
                    </option>
                  ))}
                </select>
                <p className="text-text-muted text-xs mt-2">
                  Withdrawal amount is interpreted as monthly income; we convert it based on the selected frequency.
                </p>
              </div>

              {/* Annual Increase */}
              <div>
                <label className="block text-text-secondary text-sm mb-2">Annual Increase in Withdrawal (%) <span className="text-text-muted">Range: 0% to 15%</span></label>
                <input
                  type="text"
                  value={annualIncreaseInput}
                  onChange={handlePercentChange(setAnnualIncrease, setAnnualIncreaseInput, 'annualIncrease', 0, 15)}
                  onBlur={handlePercentBlur(setAnnualIncrease, annualIncreaseInput, setAnnualIncreaseInput, 'annualIncrease', 0, 15, 6)}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors.annualIncrease ? 'border-red-500' : 'border-white/10'}`}
                  data-testid="swp-annual-increase"
                />
                {errors.annualIncrease && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {errors.annualIncrease}
                  </p>
                )}
                <input
                  type="range"
                  min="0"
                  max="15"
                  step="0.1"
                  value={annualIncrease}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    setAnnualIncrease(value);
                    setAnnualIncreaseInput(value.toString());
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="glass-effect rounded-xl p-6 border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <Wallet className="h-5 w-5 text-primary" />
                  <p className="text-text-secondary text-sm">Corpus left after {durationYears} years</p>
                </div>
                <p className="text-2xl font-bold text-text-primary">{formatCurrency(results.corpusLeft)}</p>
              </div>
              <div className="glass-effect rounded-xl p-6 border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingDown className="h-5 w-5 text-green-400" />
                  <p className="text-text-secondary text-sm">Total withdrawn</p>
                </div>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(results.totalWithdrawn)}</p>
              </div>
              <div className="glass-effect rounded-xl p-6 border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <CalendarClock className="h-5 w-5 text-secondary" />
                  <p className="text-text-secondary text-sm">When corpus will run out</p>
                </div>
                <p className="text-xl font-bold text-secondary">{runOutText}</p>
              </div>
              <div className="glass-effect rounded-xl p-6 border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  <p className="text-text-secondary text-sm">Safe withdrawal (monthly)</p>
                </div>
                <p className="text-2xl font-bold text-primary">{formatCurrency(safeMonthlyWithdrawal)}</p>
              </div>
            </div>

            {/* Insights */}
            <div className="glass-effect rounded-2xl p-6">
              <h3 className="font-heading text-xl font-bold text-text-primary mb-4">Insights</h3>
              <div className="space-y-3">
                {insights.map((text, idx) => (
                  <div key={idx} className="glass-effect rounded-xl p-4 border-l-4 border-primary">
                    <p className="text-text-secondary">{text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Chart */}
            <div className="glass-effect rounded-2xl p-6">
              <h3 className="font-heading text-xl font-bold text-text-primary mb-4">Year-wise Trend</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
              <LineChart data={results.yearlyData} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="year" stroke="#94a3b8" fontSize={12} />
                    <YAxis
                      stroke="#94a3b8"
                      fontSize={12}
                      domain={[0, 'dataMax']}
                      tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`}
                    />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line type="monotone" dataKey="corpus" name="Corpus Remaining" stroke="#F5A623" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="totalWithdrawn" name="Total Withdrawn" stroke="#94a3b8" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Year-wise Table */}
        <div className="mt-8 glass-effect rounded-2xl p-6">
          <h3 className="font-heading text-xl font-bold text-text-primary mb-4">Year-wise Breakdown</h3>
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="sticky top-20 bg-background z-10 text-left py-3 px-4 text-text-muted font-medium">Year</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Corpus Remaining</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Total Withdrawn</th>
              </tr>
            </thead>
            <tbody>
              {visibleYearRows.map((row, index) => (
                <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-3 px-4 text-text-primary">{row.year}</td>
                  <td className="py-3 px-4 text-right text-primary font-medium">{formatCurrency(row.corpus)}</td>
                  <td className="py-3 px-4 text-right text-green-400">{formatCurrency(row.totalWithdrawn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 flex justify-center gap-3">
            {tableVisibleRows < results.yearlyData.length && (
              <button
                className="px-4 py-2 bg-white/5 hover:bg-white/10 text-text-secondary rounded-lg border border-white/10 transition-colors"
                onClick={() => setTableVisibleRows((prev) => Math.min(prev + 15, results.yearlyData.length))}
              >
                Show More
              </button>
            )}
            {tableVisibleRows > 15 && (
              <button
                className="px-4 py-2 bg-white/5 hover:bg-white/10 text-text-secondary rounded-lg border border-white/10 transition-colors"
                onClick={() => setTableVisibleRows(15)}
              >
                Show Less
              </button>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 glass-effect rounded-xl p-4 border border-yellow-500/30">
          <p className="text-text-muted text-sm">
            <span className="text-yellow-500 font-semibold">Disclaimer:</span> The calculations shown are based on the inputs provided and assume constant returns throughout the investment period. Actual returns may vary based on market conditions, fund performance, and other factors. These projections are for illustrative purposes only and should not be considered as financial advice. Please consult a qualified financial advisor before making investment decisions.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <div className="glass-effect rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">Get a Personalized Investment Plan</h3>
            <p className="text-text-secondary mb-6">Let us help you create a customized SIP strategy based on your financial goals</p>
            <Link to="/contact">
              <Button className="btn-primary text-lg px-8 py-4">
                Get Started <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
